/* =========================================
   SEARCH PAGE LOADER
   ========================================= */
document.addEventListener('DOMContentLoaded', () => {
  const gridContainer = document.querySelector('[data-search-loading]');
  if (gridContainer) gridContainer.classList.add('is-loading');

  const hasSearchResults = document.querySelector('[data-search-loading]');
  if (!hasSearchResults) {
    const loader = document.getElementById('search-page-loader');
    if (loader) loader.style.display = 'none';
  }
});

class MainSearch extends SearchForm {
  constructor() {
    super();

    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.expandedCategories = new Set();
    this.cachedGroupedResults = {};
    this.activeFilter = 'all';
    this.currentPages = {};
    this.itemsPerPage = 20;

    this.setupEventListeners();

    if (this.redirectIfUnnormalized()) return;

    setTimeout(() => {
      this.applyExactSearchLogic();
    }, 50);

    document.addEventListener('shopify:section:load', () => {
      this.applyExactSearchLogic();
    });
  }

  redirectIfUnnormalized() {
    const params = new URLSearchParams(window.location.search);
    const term = params.get('q');
    if (!term) return false;

    const normalized = this.normalizeSearchTerm(term);

    if (normalized !== term) {
      params.set('q', normalized);
      window.location.replace(window.location.pathname + '?' + params.toString());
      return true;
    }

    return false;
  }

  setupEventListeners() {
    const allSearchForms = [];

    this.allSearchInputs.forEach((input) => {
      if (input.form) allSearchForms.push(input.form);
    });

    this.input.addEventListener('focus', this.onInputFocus.bind(this));

    allSearchForms.forEach((form) => {
      form.addEventListener('submit', () => {
        const input = form.querySelector('input[name="q"]');

        if (input && input.value) {
          const normalized = this.normalizeSearchTerm(input.value.trim());
          if (normalized !== input.value.trim()) input.value = normalized;
        }

        const loader = document.getElementById('search-page-loader');
        if (loader) loader.style.display = 'flex';
      });
    });

    if (allSearchForms.length < 2) return;

    allSearchForms.forEach((form) => {
      form.addEventListener('reset', this.onFormReset.bind(this));
    });

    this.allSearchInputs.forEach((input) => {
      input.addEventListener('input', this.onInput.bind(this));
    });
  }

  normalizeSearchTerm(term) {
    return term
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0)
      .map((word) => this.toSingular(word))
      .join(' ');
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) this.keepInSync('', this.input);
  }

  onInput(event) {
    this.keepInSync(event.target.value, event.target);
  }

  onInputFocus() {
    if (window.innerWidth < 750) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) input.value = value;
    });
  }

  applyExactSearchLogic() {
    const params = new URLSearchParams(window.location.search);
    const term = params.get('q');

    if (!term) {
      this.unlockUI();
      return;
    }

    const searchWords = this.stripSpecialChars(term.toLowerCase())
      .split(/\s+/)
      .filter((word) => word.length > 0)
      .map((word) => this.toSingular(word));

    const cards = document.querySelectorAll('.search-product-card, .search-item-card');
    const grouped = {};

    cards.forEach((card) => {
      const li = card.closest('li');
      if (!li) return;

      const rawText = (card.dataset.searchText || '').toLowerCase();
      const textWords = this.stripSpecialChars(rawText)
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .map((word) => this.toSingular(word));

      const isMatch =
        searchWords.length === 1
          ? textWords.includes(searchWords[0])
          : searchWords.every((searchWord) => textWords.includes(searchWord));

      if (!isMatch) {
        li.style.display = 'none';
        return;
      }

      li.style.display = '';

      const type = card.dataset.productType || card.dataset.itemType || 'Other';
      if (!grouped[type]) grouped[type] = [];

      const titleElement = card.querySelector('.card__heading a, h3 a');
      const title = titleElement ? titleElement.textContent.trim() : '';

      grouped[type].push({ li, title });
    });

    Object.keys(grouped).forEach((type) => {
      grouped[type].sort((a, b) => a.title.localeCompare(b.title));
    });

    this.cachedGroupedResults = grouped;
    this.currentPages = {};

    this.buildFilterPills(grouped);
    this.renderSearchGrid();

    const totalVisible = Object.values(grouped).reduce((sum, items) => sum + items.length, 0);
    this.updateProductCount(totalVisible);
    this.unlockUI();
  }

  buildFilterPills(grouped) {
    const container = document.getElementById('search-type-filters');
    if (!container) return;

    const types = Object.keys(grouped)
      .filter((type) => grouped[type].length > 0)
      .sort((a, b) => a.localeCompare(b));

    if (!types.length) {
      container.style.display = 'none';
      return;
    }

    container.innerHTML = '';

    const allButton = document.createElement('button');
    allButton.type = 'button';
    allButton.className = `search-type-pill${this.activeFilter === 'all' ? ' active' : ''}`;
    allButton.dataset.type = 'all';
    allButton.textContent = 'All';
    container.appendChild(allButton);

    types.forEach((type) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `search-type-pill${this.expandedCategories.has(type) ? ' active' : ''}`;
      button.dataset.type = type;
      button.textContent = `${this.pluralizeType(type)} (${grouped[type].length})`;
      container.appendChild(button);
    });

    container.style.display = 'flex';

    container.querySelectorAll('.search-type-pill').forEach((button) => {
      button.addEventListener('click', () => {
        const type = button.dataset.type;

        container.querySelectorAll('.search-type-pill').forEach((btn) => {
          btn.classList.remove('active');
        });

        button.classList.add('active');

        if (type === 'all') {
          this.activeFilter = 'all';
          this.expandedCategories.clear();
          this.renderSearchGrid();
          return;
        }

        this.activeFilter = 'all';
        this.expandedCategories.clear();
        this.expandedCategories.add(type);
        this.currentPages[type] = 1;

        this.renderSearchGrid();
        this.scrollToSearchType(type);
      });
    });
  }

  getFilteredResults() {
    if (this.activeFilter === 'all') return this.cachedGroupedResults;

    const filtered = {};
    if (this.cachedGroupedResults[this.activeFilter]) {
      filtered[this.activeFilter] = this.cachedGroupedResults[this.activeFilter];
    }

    return filtered;
  }

  renderSearchGrid() {
    const grid = document.querySelector('#product-grid ul') || document.querySelector('.grid.product-grid');
    if (!grid) return;

    grid.innerHTML = '';

    const results = this.getFilteredResults();

    Object.entries(results)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([type, items]) => {
        if (!items.length) return;

        const isExpanded = this.expandedCategories.has(type);
        const canToggle = items.length > 4;
        const buttonLabel = isExpanded ? 'View less' : 'View more';
        const pluralType = this.pluralizeType(type);

        const headingLi = document.createElement('li');
        headingLi.className = 'grid__item grid__item--full search-type-heading-item';
        headingLi.dataset.searchSection = type;

        headingLi.innerHTML = `
          <div class="search-type-header" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:2rem;border-bottom:1px solid #b2b2b2;">
            <h2 class="search-group-title">
              <strong>${this.escapeHtml(pluralType)}</strong>
              <span class="search-type-count">(${items.length})</span>
            </h2>
            ${
              canToggle
                ? `
                  <button type="button" class="button button--secondary view-all-toggle" data-type="${this.escapeAttribute(type)}" aria-label="Toggle View">
                    ${buttonLabel}
                  </button>
                `
                : ''
            }
          </div>
        `;

        grid.appendChild(headingLi);

        if (canToggle && !isExpanded && this.activeFilter === 'all') {
          const swiperContainerLi = document.createElement('li');
          swiperContainerLi.className = 'grid__item grid__item--full anim-fade-in';

          swiperContainerLi.innerHTML = `
            <div class="search-swiper-container swiper" style="opacity:0;">
              <div class="swiper-wrapper">
                ${items.map((item) => `<div class="swiper-slide">${item.li.innerHTML}</div>`).join('')}
              </div>
              <div class="swiper-button-next"></div>
              <div class="swiper-button-prev"></div>
            </div>
          `;

          grid.appendChild(swiperContainerLi);
          this.initializeSwiper(swiperContainerLi.querySelector('.search-swiper-container'));
          return;
        }

        this.renderPaginatedType(grid, type, items);
      });

    this.bindToggleButtons();
    this.bindPaginationButtons();
  }

  renderPaginatedType(grid, type, items) {
    const totalPages = Math.ceil(items.length / this.itemsPerPage);
    const currentPage = this.currentPages[type] || 1;

    const start = (currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;

    items.slice(start, end).forEach((item) => {
      const gridItem = document.createElement('li');
      gridItem.className = 'grid__item anim-fade-in';
      gridItem.innerHTML = item.li.innerHTML;
      grid.appendChild(gridItem);
    });

    if (totalPages <= 1) return;

    const paginationLi = document.createElement('li');
    paginationLi.className = 'grid__item grid__item--full custom-type-pagination-wrapper';

    let pageButtons = '';

    for (let page = 1; page <= totalPages; page += 1) {
      pageButtons += `
        <button
          type="button"
          class="custom-type-page-btn${page === currentPage ? ' active' : ''}"
          data-type="${this.escapeAttribute(type)}"
          data-page="${page}"
          aria-label="Go to page ${page}"
        >
          ${page}
        </button>
      `;
    }

    paginationLi.innerHTML = `
      <div class="custom-type-pagination">
        <button
          type="button"
          class="custom-type-page-btn"
          data-type="${this.escapeAttribute(type)}"
          data-page="${currentPage - 1}"
          ${currentPage === 1 ? 'disabled' : ''}
        >
          Previous
        </button>

        ${pageButtons}

        <button
          type="button"
          class="custom-type-page-btn"
          data-type="${this.escapeAttribute(type)}"
          data-page="${currentPage + 1}"
          ${currentPage === totalPages ? 'disabled' : ''}
        >
          Next
        </button>
      </div>
    `;

    grid.appendChild(paginationLi);
  }

  bindToggleButtons() {
    document.querySelectorAll('.view-all-toggle').forEach((button) => {
      button.onclick = (event) => {
        const type = event.currentTarget.getAttribute('data-type');

        if (this.expandedCategories.has(type)) {
          this.expandedCategories.delete(type);
        } else {
          this.expandedCategories.add(type);
          this.currentPages[type] = 1;
        }

        this.renderSearchGrid();
        this.scrollToSearchType(type);
      };
    });
  }

  bindPaginationButtons() {
    document.querySelectorAll('.custom-type-page-btn').forEach((button) => {
      button.onclick = (event) => {
        const type = event.currentTarget.getAttribute('data-type');
        const page = Number(event.currentTarget.getAttribute('data-page'));

        if (!type || Number.isNaN(page) || page < 1) return;

        const totalPages = Math.ceil((this.cachedGroupedResults[type] || []).length / this.itemsPerPage);
        if (page > totalPages) return;

        this.currentPages[type] = page;
        this.expandedCategories.add(type);

        this.renderSearchGrid();
        this.scrollToSearchType(type);
      };
    });
  }

  scrollToSearchType(type) {
    requestAnimationFrame(() => {
      const section = [...document.querySelectorAll('[data-search-section]')].find(
        (element) => element.dataset.searchSection === type
      );

      if (!section) return;

      const header = document.querySelector('.template-search__header');
      const offset = header ? header.offsetHeight + 16 : 16;
      const top = section.getBoundingClientRect().top + window.pageYOffset - offset;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    });
  }

  initializeSwiper(container) {
    if (!container || typeof Swiper === 'undefined') return;

    new Swiper(container, {
      slidesPerView: 2,
      spaceBetween: 16,
      observer: true,
      observeParents: true,
      navigation: {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        750: {
          slidesPerView: 4,
          spaceBetween: 24,
        },
      },
      on: {
        init() {
          container.style.opacity = '1';
        },
      },
    });
  }

  pluralizeType(type) {
    const value = String(type || '').trim();
    if (!value) return 'Other';

    if (value.toLowerCase().endsWith('s')) return value;
    return `${value}s`;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttribute(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  stripSpecialChars(str) {
    return str.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
  }

  toSingular(word) {
    if (word.length <= 2) return word;

    if (word.endsWith('ies') && word.length > 4) return word.slice(0, -3) + 'y';
    if (word.endsWith('ves') && word.length > 4) return word.slice(0, -3) + 'f';

    if (
      word.endsWith('ches') ||
      word.endsWith('shes') ||
      word.endsWith('ses') ||
      word.endsWith('xes') ||
      word.endsWith('zes')
    ) {
      return word.slice(0, -2);
    }

    if (word.endsWith('s') && word.length > 2) return word.slice(0, -1);

    return word;
  }

  updateProductCount(total) {
    const desktopCount = document.getElementById('ProductCountDesktop');
    const mobileCount = document.getElementById('ProductCount');
    const text = `${total} exact matches`;

    if (desktopCount) desktopCount.textContent = text;
    if (mobileCount) mobileCount.textContent = text;
  }

  unlockUI() {
    const gridContainer = document.querySelector('[data-search-loading]');
    const wrapper = document.querySelector('.custom-search-container');
    const loader = document.getElementById('search-page-loader');

    if (gridContainer) gridContainer.classList.remove('is-loading');
    if (loader) loader.style.display = 'none';
    if (wrapper) wrapper.style.display = 'block';
  }
}

customElements.define('main-search', MainSearch);